import { MouseEvent } from 'react';
import { Tooltip } from 'shared-ui';

import { ReactComponent as AddIcon } from 'assets/icons/add-active.svg';
import { ReactComponent as TrashIcon } from 'assets/icons/primary-trash.svg';
import { STREFA_MAX_POZIOM } from 'api/strefy/strefy.enum';
import { useLocale } from 'hooks/useLocale/useLocale';
import { useModalState } from 'hooks/useModalState/useModalState';
import { AppMessages } from 'i18n/messages';
import { EXPAND_ORGANIZATION_ITEM_TAGS } from '../../organization/Organization.variables';

import { ZoneItemProps } from './ZoneItem.types';
import * as Styled from './ZoneItem.styles';
import { AddZoneContainer } from './addZone/AddZoneContainer';
import { DeleteZoneContainer } from './deleteZone/DeleteZoneContainer';

export const ZoneItem = ({
  zone,
  allZoneCodes,
  isModifyDisabled,
  isMonitoringActive,
  onSelectZone,
  onExpandZone,
}: ZoneItemProps) => {
  const { formatMessage } = useLocale();
  const addModal = useModalState();
  const deleteModal = useModalState();

  const isRoot = zone.parentId === null;
  const hasChildren = zone.children.length > 0;
  const isMaxLevel = zone.poziom >= STREFA_MAX_POZIOM;
  // Q7 (HSK 2026-10-25): the root takes a single child, so its "+" is shown but off while it has one.
  const isRootTaken = isRoot && hasChildren;
  const isAddDisabled = isModifyDisabled || isMaxLevel || isRootTaken;
  const isDeleteDisabled = isModifyDisabled || hasChildren;

  const modifyDisabledReason = isMonitoringActive
    ? formatMessage({ id: AppMessages['definitions.zones.monitoringActive'] })
    : formatMessage({ id: AppMessages['common.noPermissions'] });

  const addStructuralReason = isMaxLevel
    ? formatMessage({ id: AppMessages['definitions.zones.add.maxLevel'] })
    : formatMessage({ id: AppMessages['definitions.zones.add.rootTaken'] });
  const addDisabledReason = isMaxLevel || isRootTaken ? addStructuralReason : modifyDisabledReason;

  const deleteDisabledReason = hasChildren
    ? formatMessage({ id: AppMessages['definitions.zones.delete.hasChildren'] })
    : modifyDisabledReason;

  const onActionClick = (event: MouseEvent, action: () => void) => {
    event.stopPropagation();
    action();
  };

  return (
    <>
      <Styled.TreeItem
        onClick={(e) => {
          if (!EXPAND_ORGANIZATION_ITEM_TAGS.includes((e.target as HTMLElement)?.tagName)) {
            onSelectZone(zone.id);
          }
        }}
        nodeId={zone.id}
        label={
          <Styled.LabelWrapper data-testid={`zone-${zone.kod}`}>
            <Styled.TextWrapper>
              <Styled.Code>{zone.kod}</Styled.Code>
              <Styled.Name>{zone.nazwa}</Styled.Name>
            </Styled.TextWrapper>
            <Styled.Actions>
              {!isRoot && (
                <Tooltip title={isDeleteDisabled ? deleteDisabledReason : ''}>
                  <Styled.ActionWrapper>
                    <Styled.DeleteButton
                      data-testid={`delete-zone-${zone.kod}`}
                      aria-label={formatMessage({ id: AppMessages['definitions.zones.delete.title'] })}
                      disabled={isDeleteDisabled}
                      onClick={(e) => onActionClick(e, deleteModal.onModalOpen)}
                    >
                      <TrashIcon />
                    </Styled.DeleteButton>
                  </Styled.ActionWrapper>
                </Tooltip>
              )}
              <Tooltip title={isAddDisabled ? addDisabledReason : ''}>
                <Styled.ActionWrapper>
                  <Styled.AddButton
                    data-testid={`add-zone-${zone.kod}`}
                    aria-label={formatMessage({ id: AppMessages['definitions.zones.add.title'] })}
                    disabled={isAddDisabled}
                    onClick={(e) => onActionClick(e, addModal.onModalOpen)}
                  >
                    <AddIcon />
                  </Styled.AddButton>
                </Styled.ActionWrapper>
              </Tooltip>
            </Styled.Actions>
          </Styled.LabelWrapper>
        }
      >
        {hasChildren
          ? zone.children.map((child) => (
              <ZoneItem
                key={child.id}
                zone={child}
                allZoneCodes={allZoneCodes}
                isModifyDisabled={isModifyDisabled}
                isMonitoringActive={isMonitoringActive}
                onSelectZone={onSelectZone}
                onExpandZone={onExpandZone}
              />
            ))
          : null}
      </Styled.TreeItem>

      {!isAddDisabled && (
        <AddZoneContainer
          parentZone={zone}
          allZoneCodes={allZoneCodes}
          isModalOpen={addModal.isModalOpen}
          onModalClose={addModal.onModalClose}
          onZoneAdded={() => onExpandZone(zone.id)}
        />
      )}

      {!isRoot && !isDeleteDisabled && (
        <DeleteZoneContainer
          zone={zone}
          isModalOpen={deleteModal.isModalOpen}
          onModalClose={deleteModal.onModalClose}
        />
      )}
    </>
  );
};
